/**
 * Generates a greeting message based on the current time of day.
 *
 * @return {string} The greeting message based on the time of day.
 */
const getGreeting = () => {
	const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const currentTime = new Date().toLocaleString('en-US', {
		timeZone: userTimezone
	});
	const currentHour = new Date(currentTime).getHours();

	const isMorning = currentHour >= 4 && currentHour <= 10;
	const isAfternoon = currentHour >= 10 && currentHour < 15;
	const isEvening = currentHour >= 15 && currentHour < 18;

	if (isMorning) {
		return 'Selamat Pagi';
	} else if (isAfternoon) {
		return 'Selamat Siang';
	} else if (isEvening) {
		return 'Selamat Sore';
	} else {
		return 'Selamat Malam';
	}
};

/**
 * Check if the device is touch capable.
 *
 * This function checks if the device is touch capable by checking if the
 * `ontouchstart` event is supported in the browser, or if the device has
 * touch points.
 *
 * @return {boolean} The result of the check.
 */
const isTouchDevice = () => {
	return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const priceFormatter = new Intl.NumberFormat('id-ID', {
	style: 'currency',
	currency: 'IDR',
	minimumFractionDigits: 0,
	maximumFractionDigits: 0
});

/**
 * Format a number to a short currency string using the following rules:
 *   - Small numbers (0.001 to 999) are formatted as a number with the specified precision
 *   - Medium numbers (1,000 to 999,999) are formatted as a number with the specified precision, followed by "K" for thousands
 *   - Large numbers (1,000,000 to 999,999,999) are formatted as a number with the specified precision, followed by "M" for millions
 *   - Very large numbers (1,000,000,000 and larger) are formatted as a number with the specified precision, followed by "B" for billions
 *
 * @param {number} num The number to format
 * @param {number} [precision=1] The maximum number of decimal places to display
 * @return {string} The formatted string
 */
function formatShortCurrency(num: number, precision: number = 1): string {
	// Define a list of suffixes for different scales
	const suffixes = ['', 'K', 'M', 'B', 'T', 'P', 'E']; // T for trillion, P for peta, E for exa
	const absNum = Math.abs(num);

	// Determine the suffix index based on the size of the number
	const suffixIndex = absNum === 0 ? 0 : Math.floor(Math.log10(absNum) / 3);

	// Calculate the value to display based on the suffix
	const value = num / Math.pow(1000, suffixIndex);

	// Handle very small numbers, e.g., 0.001 as 1m (micro)
	if (absNum < 1 && absNum > 0) {
		return (
			new Intl.NumberFormat('id-ID', { maximumFractionDigits: precision }).format(num * 1_000_000) +
			'µ'
		); // µ for micro
	}

	// Format the number using the Indonesian locale
	const formattedNumber = new Intl.NumberFormat('id-ID', {
		maximumFractionDigits: precision
	}).format(value);

	return formattedNumber + suffixes[suffixIndex];
}

/**
 * Returns the percentage of the given price that is discounted by the given discount price.
 * @param price - The original price.
 * @param discountPrice - The discounted price.
 * @returns The percentage of the original price that is discounted.
 */
const getDiscountPercentage = (price: number, discountPrice: number): number => {
	const isInvalid = price <= 0 || discountPrice <= 0;

	if (isInvalid) return 0;

	const discountAmount = price - discountPrice;
	const discountPercentage = (discountAmount / price) * 100;

	return Math.round(discountPercentage);
};

/**
 * Convert a string to a slug. This function takes a string and returns a
 * version of it that is suitable for use as a URL slug. It removes all
 * non-word characters, converts all spaces to hyphens, and trims any leading
 * or trailing hyphens.
 *
 * @param {string} inputString The string to convert to a slug
 * @return {string} The slugified string
 */
function slugify(text: string): string {
	return text
		.toString()
		.toLowerCase()
		.replace(/\s+/g, '-') // Replace spaces with hyphens
		.replace(/[^\w-]+/g, '') // Remove all non-word characters
		.replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
		.replace(/^-+/, '') // Trim leading hyphens
		.replace(/-+$/, ''); // Trim trailing hyphens
}

/**
 * Capitalize each word in a string.
 *
 * This function takes a string and returns a new string with each word
 * capitalized. It does this by splitting the string into words, capitalizing
 * the first letter of each word, and then joining the words back together.
 *
 * @param {string} text The string to capitalize
 * @return {string} The string with each word capitalized
 */
function capitalizeWords(text: string): string {
	return text
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

/**
 * Return a string with only numbers input.
 *
 * This function takes a string and returns a new string with all
 * non-numeric characters removed. It does this by using a regular
 * expression to match all characters that are not numbers and
 * replacing them with an empty string.
 *
 * @param {string} value The string to return numbers from
 * @return {string} The string with only numbers
 */
function onlyNumbersInput(value: string): string {
	return value.replace(/[^0-9]/g, '');
}

/**
 * Transform a string into a suitable username by transforming it to lowercase
 * and removing all spaces and special characters.
 *
 * @param {string} name The string to transform
 * @return {string} The transformed string
 */
function usernamify(name: string) {
	// transform all to lowercase and remove spaces and special characters
	return name.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Check if any of the given elements are covered by the given parent element.
 *
 * This function uses the `getBoundingClientRect()` method to get the bounding rectangle of the parent element and each of the elements. It then checks if any of the elements is outside of the parent's visible area (for horizontal scrolling) by comparing the left and right properties of the bounding rectangles. If any of the elements is found to be outside of the parent's visible area, the function immediately returns `true`. If no elements are found to be outside of the parent's visible area, the function returns `false`.
 *
 * @param {NodeListOf<HTMLElement>} Elements The elements to check
 * @param {HTMLElement} parent The parent element
 * @return {boolean} Whether any of the elements are covered by the parent element
 */
function isElementsCoveredByParent(Elements: NodeListOf<HTMLElement>, parent: HTMLElement) {
	const parentRect = parent.getBoundingClientRect();

	for (const item of Elements) {
		const swiperRect = item.getBoundingClientRect();

		// Check if the swiper item is within the parent's visible area (for horizontal scrolling)
		if (swiperRect.left < parentRect.left || swiperRect.right > parentRect.right) {
			return true; // Exit as soon as a covered item is found
		}
	}

	return false; // Return false if no covered item is found
}

/**
 * Scrolls to the specified element with an offset.
 *
 * @param {HTMLElement} element The element to scroll to
 * @param {number} offset The offset to adjust the scroll position
 */
function scrollWithOffset(element: HTMLElement, offset: number) {
	const yCoordinate = element.getBoundingClientRect().top + window.pageYOffset;
	const yOffset = -offset;

	scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
}

/**
 * Split the input text into two halves and return them as an array of strings.
 *
 * @param {string} text The text to split into halves
 * @return {string[]} An array containing the first and second halves of the input text
 */
function getHalfText(text: string): string[] {
	const textArray = text.split(' ');
	const middleIndex = Math.floor(textArray.length / 2);
	const firstHalf = textArray.slice(0, middleIndex);
	const secondHalf = textArray.slice(middleIndex);

	return [firstHalf.join(' '), secondHalf.join(' ')];
}

/**
 * Reverse the effects of the `slugify` function. This function takes a slugified
 * string and returns a string with each word capitalized and separated by
 * spaces instead of hyphens.
 *
 * @param {string} text The slugified string to reverse
 * @return {string} The string with each word capitalized and separated by spaces
 */
function unslugify(text: string): string {
	return text
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

/**
 * Returns an object with boolean properties indicating whether the given userAgent matches the OS type.
 *
 * @param {string | null} userAgent The user agent string to test
 * @return {{ [OS in 'Android' | 'iOS' | 'Windows' | 'Mac' | 'Linux']: boolean }}
 */
function checkDevice(userAgent: string | null): {
	[OS in 'Android' | 'iOS' | 'Windows' | 'Mac' | 'Linux']: boolean;
} {
	if (typeof userAgent !== 'string') {
		throw new TypeError('userAgent must be a string');
	}

	const osRegexMap = {
		Android: /Android/i,
		iOS: /iPhone|iPad|iPod/i,
		Windows: /Windows/i,
		Mac: /Macintosh/i,
		Linux: /Linux/i
	};

	return Object.fromEntries(
		Object.entries(osRegexMap).map(([os, regex]) => [os, regex.test(userAgent)])
	) as { [OS in 'Android' | 'iOS' | 'Windows' | 'Mac' | 'Linux']: boolean };
}

/**
 * Creates a debounced async function that delays the execution of the given function
 * until the given delay has passed since the last call.
 *
 * The function will only be executed once, even if it is called multiple times within
 * the delay period.
 *
 * @param {(...args: A) => Promise<void>} func The function to debounce
 * @param {number} delay The delay period in milliseconds
 * @returns {(...args: A) => Promise<void>} The debounced function
 */
const debounceAsync = <A extends unknown[]>(
	func: (...args: A) => Promise<void>,
	delay: number
): ((...args: A) => Promise<void>) => {
	let timer: ReturnType<typeof setTimeout>;
	let isExecuting = false;

	return async (...args: A) => {
		clearTimeout(timer);

		timer = setTimeout(async () => {
			if (isExecuting) return; // Prevent overlapping execution

			isExecuting = true;

			try {
				await func(...args);
			} finally {
				isExecuting = false;
			}
		}, delay);
	};
};

/**
 * Determines if a hex color is dark.
 *
 * This function calculates the brightness of a hex color using the
 * luminance formula for RGB colors, which assigns different weights
 * to the red, green, and blue components. It compares the resulting
 * brightness with a threshold to determine if the color is dark.
 *
 * @param {string} hex - The hex color code, which can be in 3 or 6 character format.
 * @returns {boolean} True if the color is considered dark, otherwise false.
 * @throws Will throw an error if the hex color format is invalid.
 */

function isHexColorDark(hex: string): boolean {
	hex = hex.replace(/^#/, '');
	if (![3, 6].includes(hex.length)) throw new Error('Invalid hex color format');

	const [r, g, b] = (hex.length === 3 ? [...hex].map((c) => c + c) : hex.match(/.{2}/g)!)
		.map((c) => parseInt(c, 16))
		.map((v) => (v / 255 <= 0.03928 ? v / 255 / 12.92 : ((v / 255 + 0.055) / 1.055) ** 2.4));

	const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
	return luminance < 0.5; // Batas 0.5 lebih akurat dibandingkan 128
}

export {
	getGreeting,
	isTouchDevice,
	priceFormatter,
	formatShortCurrency,
	getDiscountPercentage,
	slugify,
	capitalizeWords,
	onlyNumbersInput,
	usernamify,
	isElementsCoveredByParent,
	scrollWithOffset,
	getHalfText,
	unslugify,
	checkDevice,
	debounceAsync,
	isHexColorDark
};
